import { GetStaticProps, GetStaticPaths } from "next"
import { Anchor } from "../../app/components/anchor"
import Head from "next/head"
import PageHead from "../../components/head/PageHead"
import PageNav from "../../storyblok/sections/PageNav"
import Footer from "../../storyblok/static/Footer"
import toolsData from "../../constants/tools/tools.json"
import { useEffect, useRef, useState } from "react"

interface ToolPageProps {
  toolData: typeof toolsData[keyof typeof toolsData]
}

export default function ToolPage({ toolData }: ToolPageProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [iframeHeight, setIframeHeight] = useState<string>("800px")

  useEffect(() => {
    // For tools with dynamic height, use postMessage
    const handleMessage = (event: MessageEvent) => {
      // Check if the message is from the expected origin
      if (event.origin === new URL(toolData.iframeUrl).origin) {
        if (typeof event.data === "object" && event.data.type === "resize") {
          const height = event.data.height
          if (height) {
            setIframeHeight(`${Number(height) + 20}px`)
          }
        }
      }
    }

    // Request initial height
    setTimeout(() => {
      iframeRef.current?.contentWindow?.postMessage(
        { type: "requestHeight" },
        new URL(toolData.iframeUrl).origin,
      )
    }, 1000)

    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [toolData.iframeUrl])

  // Handle responsive heights if defined
  useEffect(() => {
    if (toolData.responsiveHeight) {
      const updateHeight = () => {
        const width = window.innerWidth
        let height = toolData.responsiveHeight.xl // default to largest size

        if (width < 390) {
          height = toolData.responsiveHeight.xs || height
        } else if (width < 640) {
          height = toolData.responsiveHeight.sm || height
        } else if (width < 768) {
          height = toolData.responsiveHeight.md || height
        } else if (width < 1024) {
          height = toolData.responsiveHeight.lg || height
        } else if (width < 1280) {
          height = toolData.responsiveHeight.xl || height
        }

        setIframeHeight(height)
      }

      // Initial height
      updateHeight()

      // Update height on resize
      window.addEventListener("resize", updateHeight)
      return () => window.removeEventListener("resize", updateHeight)
    }
  }, [toolData.responsiveHeight])

  return (
    <main>
      <Head>
        <link rel="preload" href={toolData.iframeUrl} as="document" />
        <link rel="dns-prefetch" href={new URL(toolData.iframeUrl).origin} />
        <link
          rel="preconnect"
          href={new URL(toolData.iframeUrl).origin}
          crossOrigin="anonymous"
        />
      </Head>

      <div className="flex flex-col min-h-screen">
        <PageNav noBlok buttonLabel="Get Transportation Quotes" />
        <PageHead
          title={toolData.metaTitle}
          description={toolData.metaDescription}
        />

        <main className="flex-1 flex flex-col">
          <div className="pt-20 md:pt-24 px-4 lg:px-10 lg:mx-auto lg:max-w-[1300px]">
            <nav aria-label="breadcrumb" className="flex items-center mb-8">
              <div className="flex items-center p-0 m-0 font-montserrat text-gray9 overflow-x-auto">
                <ol className="items-center p-0 m-0 flex">
                  <li className="whitespace-nowrap">
                    <Anchor href="/">Home</Anchor>
                    <span className="mx-2">/</span>
                  </li>
                  <li className="whitespace-nowrap">
                    <Anchor href="/tools">Tools</Anchor>
                    <span className="mx-2">/</span>
                  </li>
                  <li className="whitespace-nowrap font-semibold">
                    <span>{toolData.title}</span>
                  </li>
                </ol>
              </div>
            </nav>

            <h1 className="text-4xl font-bold mb-8">{toolData.title}</h1>
            <p className="text-xl text-gray-600 mb-12">
              {toolData.description}
            </p>

            <div className="flex-1 w-full mb-12">
              <iframe
                ref={iframeRef}
                src={toolData.iframeUrl}
                className="w-full"
                title={toolData.title}
                allowFullScreen
                scrolling="no"
                style={{
                  overflow: "hidden",
                  height: iframeHeight,
                  transition: "height 0.3s ease-in-out",
                }}
                loading="eager"
              />
            </div>
          </div>
        </main>

        <Footer noBlok />
      </div>
    </main>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = Object.keys(toolsData).map((tool) => ({
    params: { tool },
  }))

  return {
    paths,
    fallback: false,
  }
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const toolId = params?.tool as string
  const toolData = toolsData[toolId as keyof typeof toolsData]

  if (!toolData) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      toolData,
    },
    revalidate: 60 * 60 * 24,
  }
}
